"use strict";
'use es6';

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard");

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));

require("../listeners/focusStylesListener");

var _classnames = _interopRequireDefault(require("classnames"));

var _I18n = _interopRequireDefault(require("I18n"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _react = _interopRequireWildcard(require("react"));

var _ButtonConstants = require("../button/ButtonConstants");

var _ShareButton = _interopRequireDefault(require("../decorators/ShareButton"));

var _useActive2 = _interopRequireDefault(require("../hooks/useActive"));

var _UIIcon = _interopRequireDefault(require("../icon/UIIcon"));

var _setupStyledComponents = require("../listeners/setupStyledComponents");

var _UITruncateString = _interopRequireDefault(require("../text/UITruncateString"));

var _Links = require("../utils/Links");

var _decorators = require("../utils/propTypes/decorators");

var _refObject = _interopRequireDefault(require("../utils/propTypes/refObject"));

var _tshirtSize = require("../utils/propTypes/tshirtSize");

var _validHref = _interopRequireDefault(require("../utils/propTypes/validHref"));

var _TabIndex = require("../utils/TabIndex");

var _UnsafeUrl = require("../utils/UnsafeUrl");

var _UIClickable = _interopRequireDefault(require("./UIClickable"));

(0, _setupStyledComponents.setupStyledComponents)();

var getUse = function getUse(use) {
  return use && _ButtonConstants.USE_CLASSES[use] ? use : 'secondary';
};

var renderContents = function renderContents(children) {
  var stringBuffer = '';
  var nextChildIndex = 0;

  var childArr = _react.Children.toArray(children);

  return _react.Children.map(children, function (child) {
    nextChildIndex += 1;

    if (child == null) {
      return null;
    } // in response to a breaking change in React 0.15
    // (https://facebook.github.io/react/blog/2016/04/07/react-v15.html#no-more-extra-ltspangts)
    // this wraps any bare strings within a <span> to provide a more
    // predictable DOM for applying automatic spacing rules.


    if (typeof child === 'string') {
      stringBuffer += child;

      if (childArr.length < nextChildIndex || typeof childArr[nextChildIndex] !== 'string') {
        var wrappedStringBuffer = _react.default.createElement("span", null, stringBuffer);

        stringBuffer = '';
        return wrappedStringBuffer;
      } else {
        return null;
      }
    }

    return child;
  });
};

var renderExternalButtonIcon = function renderExternalButtonIcon(external, iconProps) {
  if (!external) return null; // Opening links in a new window is an accessibility anti-pattern.
  // To mitigate this, we add accessible text in the icon to warn that
  // the link will be opened in a new window
  // @link http://diveintoaccessibility.info/day_16_not_opening_new_windows.html

  return _react.default.createElement(_UIIcon.default, Object.assign({}, iconProps, {
    "aria-label": _I18n.default.text('salesUI.UILink.externalTitle'),
    className: iconProps && iconProps.className || 'private-link--external__icon',
    name: "externalLink"
  }));
};

var renderTruncatedContents = function renderTruncatedContents(truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon) {
  return truncate ? _react.default.createElement(_UITruncateString.default, Object.assign({
    innerClassName: "private-button__truncate-string",
    useFlex: true,
    fixedChildren: renderedExternalButtonIcon,
    tooltip: truncateTooltip || renderedContents
  }, truncateStringProps), _react.default.createElement("span", null, renderedContents)) : renderedContents;
};

var UIButton = function UIButton(props) {
  var _useNativeButton = props._useNativeButton,
      ariaPressed = props['aria-pressed'],
      __active = props.active,
      autoFocus = props.autoFocus,
      block = props.block,
      buttonRef = props.buttonRef,
      children = props.children,
      className = props.className,
      disabled = props.disabled,
      external = props.external,
      href = props.href,
      iconProps = props.iconProps,
      onClick = props.onClick,
      rel = props.rel,
      responsive = props.responsive,
      size = props.size,
      tabIndex = props.tabIndex,
      target = props.target,
      truncate = props.truncate,
      truncateStringProps = props.truncateStringProps,
      truncateTooltip = props.truncateTooltip,
      type = props.type,
      use = props.use,
      rest = (0, _objectWithoutProperties2.default)(props, ["_useNativeButton", "aria-pressed", "active", "autoFocus", "block", "buttonRef", "children", "className", "disabled", "external", "href", "iconProps", "onClick", "rel", "responsive", "size", "tabIndex", "target", "truncate", "truncateStringProps", "truncateTooltip", "type", "use"]);

  var _useActive = (0, _useActive2.default)(props),
      active = _useActive.active,
      onMouseDown = _useActive.onMouseDown,
      onKeyDown = _useActive.onKeyDown,
      onKeyUp = _useActive.onKeyUp,
      onBlur = _useActive.onBlur;

  (0, _react.useEffect)(function () {
    // Simulate autoFocus when rendering a link element
    if (autoFocus && href) {
      buttonRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleClick = function handleClick(evt) {
    // Block click events while disabled, since we don't actually use [disabled] in the DOM.
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
    } else {
      if (onClick) onClick(evt);
    }
  };

  var handleKeyDown = function handleKeyDown(evt) {
    if (onKeyDown) onKeyDown(evt); // Ignore keydown events that bubbled up

    if (evt.target !== buttonRef.current) return; // If this is an <a> with a click handler, simulate click on Spacebar to be more button-like

    if (onClick && href) {
      if (evt.key === ' ') {
        // Prevent scrolling
        evt.preventDefault();

        if (disabled) {
          evt.stopPropagation();
        } else {
          onClick(evt);
        }
      }
    }
  };

  var shorthandSize = (0, _tshirtSize.toShorthandSize)(size);
  var computedTabIndex = (0, _TabIndex.getTabIndex)(disabled, tabIndex);
  var computedTarget = (0, _Links.getTarget)(external, target);
  var computedUse = getUse(use);

  if (process.env.NODE_ENV !== 'production') {
    (0, _Links.warnIfShouldBeExternal)(external, computedTarget, 'UIButton');
  }

  var renderedContents = renderContents(children);
  var renderedExternalButtonIcon = renderExternalButtonIcon(external, iconProps);
  var wrappedContents = renderTruncatedContents(truncate, truncateStringProps, truncateTooltip, renderedContents, renderedExternalButtonIcon);
  var unstyled = computedUse === 'unstyled';
  var computedClassName = unstyled ? (0, _classnames.default)(className, _ButtonConstants.USE_CLASSES[computedUse]) : (0, _classnames.default)('uiButton private-button', _ButtonConstants.USE_CLASSES[computedUse], _ButtonConstants.SIZE_CLASSES[shorthandSize] || _ButtonConstants.SIZE_CLASSES.default, className, {
    'private-button--active': active,
    'private-button--block': block,
    'disabled private-button--disabled': disabled,
    'private-button--non-responsive': !responsive,
    'private-button--non-link': _ButtonConstants.LINK_USES.indexOf(computedUse) === -1
  });
  var commonProps = Object.assign({}, rest, {
    'aria-disabled': disabled,
    className: computedClassName,
    'data-button-use': computedUse,
    onBlur: onBlur,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: onKeyUp,
    onMouseDown: onMouseDown,
    ref: buttonRef,
    tabIndex: computedTabIndex
  });

  if (href) {
    var computedRel = (0, _Links.getRel)(rel, computedTarget);
    return (0, _Links.renderSecurityTaggedLink)(href, _react.default.createElement("a", Object.assign({}, commonProps, {
      href: (0, _UnsafeUrl.getSafeUrl)(href),
      rel: computedRel,
      target: computedTarget
    }), wrappedContents, truncate || renderedExternalButtonIcon));
  }

  var ButtonComponent = _useNativeButton ? 'button' : _UIClickable.default;

  if (!_useNativeButton) {
    // Pass buttonRef down directly to the UIClickable
    commonProps.buttonRef = buttonRef;
    delete commonProps.ref;
  }

  return _react.default.createElement(ButtonComponent, Object.assign({}, commonProps, {
    type: type,
    "aria-pressed": ariaPressed != null ? ariaPressed : active || undefined,
    autoFocus: autoFocus,
    disabled: _useNativeButton ? undefined : disabled,
    onKeyDown: onKeyDown
  }), wrappedContents);
};

UIButton.propTypes = {
  _useNativeButton: _propTypes.default.bool,
  active: (0, _decorators.hidden)(_propTypes.default.bool),
  autoFocus: _propTypes.default.bool,
  'aria-pressed': _propTypes.default.bool,
  block: _propTypes.default.bool,
  buttonRef: _refObject.default,
  children: _propTypes.default.node,
  disabled: _propTypes.default.bool.isRequired,
  external: _propTypes.default.bool.isRequired,
  hovered: (0, _decorators.hidden)(_propTypes.default.bool),
  href: _validHref.default,
  iconProps: _propTypes.default.object,
  responsive: _propTypes.default.bool.isRequired,
  rel: _propTypes.default.string,
  size: (0, _tshirtSize.propTypeForSizes)(['xs', 'sm', 'md'], ['default']),
  target: _propTypes.default.string,
  truncate: _propTypes.default.bool.isRequired,
  truncateStringProps: _propTypes.default.object,
  truncateTooltip: _propTypes.default.node,
  type: _propTypes.default.oneOf(['button', 'reset', 'submit']).isRequired,
  use: _propTypes.default.oneOf(Object.keys(_ButtonConstants.USE_CLASSES)).isRequired
};
UIButton.defaultProps = {
  _useNativeButton: true,
  disabled: false,
  external: false,
  responsive: true,
  size: 'md',
  truncate: false,
  type: 'button',
  use: 'secondary'
};
UIButton.displayName = 'UIButton';

var _default = (0, _ShareButton.default)(UIButton);

exports.default = _default;
module.exports = exports.default;